import type { ReactNode } from "react";

/**
 * Strich-Bausteine (Kantine „Snacks und Getränke“ v2, Betreiber 14.10.2026 „W159 Empfehlung“ = Vorschläge 1 bis 9, KN1 bis KN10;
 * Design-Guide `strich-kachel.md`, Muster M-081, Abweichung AW-097). Rein darstellend: Zustand, Aktion und Rechtefrage gehören
 * dem Aufrufer (Anbieten = Erlauben, r110); hier steht nur die Form.
 *
 *  - `StrichRaster` + `StrichKachel`: EINE Struktur, zwei Gesichter. Ab `lg` ein Kachelraster (Symbol, Name, Preis, Plus; ganze
 *    Kachel = ein echter `<button>`), darunter eine kompakte Liste in einer Karte (ganze Zeile antippbar, Name links, Preis
 *    vor dem Plus, mindestens 44 px Ziel). Ein Element, nicht zwei: kein doppeltes Markup, kein doppelter Tab-Stop.
 *  - `StrichNurZeile`: dieselbe Zeile ohne Bedienelement (Zustand „nicht buchbar“, Name links, Preis rechts).
 *  - `KategorieLeiste`: Sprungmarken als Anker-Links; hängt unter der Kopfzeile (sticky), horizontal scrollbar.
 *  - `HeuteLeiste`: schmale feste Leiste über der unteren Tab-Leiste (nur unter `lg`), `role="status"`.
 *
 * Abweichung AW-097: Eigenbau aus Preline-Klassen (Kachel/Zeile nach `buttons-white-color-variants` und der Pille
 * `badge-soft-color-variants`, Leiste nach den Unterleisten-Mustern), kein Katalogblock 1:1; siehe
 * `docs/ui-preline/abweichungen.json` und `docs/ui-preline/designguide/strich-kachel.md`.
 */

// Raster: Auswahl 4 je Zeile ab lg; Sortiment 3 je Zeile ab lg, 4 ab 2xl (Tailwind-Standard, kein eigener Breakpoint).
const RASTER = {
  auswahl: "bg-card border border-card-line shadow-2xs rounded-xl overflow-hidden lg:bg-transparent lg:border-0 lg:shadow-none lg:rounded-none lg:overflow-visible lg:grid lg:gap-3 lg:grid-cols-4",
  sortiment:
    "bg-card border border-card-line shadow-2xs rounded-xl overflow-hidden lg:bg-transparent lg:border-0 lg:shadow-none lg:rounded-none lg:overflow-visible lg:grid lg:gap-3 lg:grid-cols-3 2xl:grid-cols-4",
} as const;

export function StrichRaster({ art, children, ariaLabel }: { art: keyof typeof RASTER; children: ReactNode; ariaLabel?: string }) {
  return (
    <>
      {/* Preline: component/buttons/buttons-white-color-variants (Kachel), data-display/data-display-sub-operational-activity-views/data-display-cat-list-groups/brand-logo-list (Liste) */}
      <ul className={RASTER[art]} aria-label={ariaLabel}>
        {children}
      </ul>
    </>
  );
}

// Ganze Ketten (nie zusammengesetzt, R8). Ton „Gebucht“ = teal-100/200/800 wie `Alert` erfolg (Original component/alerts/alerts-soft-color-variants);
// die Farbe trägt nie allein: Haken und Wort stehen daneben.
const KACHEL_RUHE =
  "w-full flex items-center gap-x-3 px-4 min-h-13 text-start text-foreground hover:bg-layer-hover focus:bg-layer-focus focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none lg:p-3 lg:min-h-16 lg:rounded-xl lg:border lg:border-layer-line lg:bg-layer lg:shadow-2xs";
const KACHEL_GEBUCHT =
  "w-full flex items-center gap-x-3 px-4 min-h-13 text-start bg-teal-100 text-teal-800 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none lg:p-3 lg:min-h-16 lg:rounded-xl lg:border lg:border-teal-200 lg:shadow-2xs";
const SYMBOL_RUHE = "max-lg:hidden shrink-0 flex justify-center items-center size-9 rounded-lg bg-surface text-muted-foreground-1";
const SYMBOL_GEBUCHT = "max-lg:hidden shrink-0 flex justify-center items-center size-9 rounded-lg bg-teal-100 text-teal-800";
const PREIS_RUHE = "block shrink-0 text-sm tabular-nums text-muted-foreground-1 lg:text-xs";
const PREIS_GEBUCHT = "block shrink-0 text-sm tabular-nums text-teal-800 lg:text-xs";

export function StrichKachel({
  name,
  preis,
  symbol,
  gebucht,
  laedt,
  onClick,
  ariaLabel,
  meldung,
  ausverkauft,
}: {
  name: string;
  /** Preis als fertiger Text („0,80 €“). */
  preis: string;
  /** Kategorie-Symbol (Lucide, `size-4`, vom Aufrufer); nur ab `lg` sichtbar. */
  symbol: ReactNode;
  /** „Gebucht“ steht statt des Plus (Haken und Wort). */
  gebucht: boolean;
  /** Ein Vorgang läuft: Kachel gesperrt (zweiter Klick wartet, ein Doppelklick ist EINE Buchung). */
  laedt: boolean;
  onClick: () => void;
  /** Zugänglicher Name („Strich für Kaffee, 0,80 €“). */
  ariaLabel: string;
  /** Satz am Feld (Ablehnung oder Fehlschlag), unter der Kachel; vom Aufrufer als `Alert` gesetzt. */
  meldung?: ReactNode;
  /**
   * Bestandsführung (Paket 1, Betreiber 19.10.2026, Idee 2): nur das WORT „ausverkauft“ (nie eine Zahl). Die Kachel ist dann nicht buchbar, das Wort
   * steht statt des Plus. Der Aufrufer setzt es nur, wenn der Bereich „Ausverkauft am Terminal zeigen“ eingeschaltet hat (additiv).
   */
  ausverkauft?: boolean;
}) {
  return (
    <li className="border-b last:border-b-0 border-line-2 lg:border-b-0">
      <button type="button" className={gebucht ? KACHEL_GEBUCHT : KACHEL_RUHE} onClick={onClick} disabled={laedt || ausverkauft === true} aria-label={ausverkauft ? `${ariaLabel}, ausverkauft` : ariaLabel}>
        <span className={gebucht ? SYMBOL_GEBUCHT : SYMBOL_RUHE} aria-hidden="true">
          {symbol}
        </span>
        <span className="contents lg:block lg:grow lg:min-w-0">
          <span className="block grow min-w-0 truncate text-sm font-medium lg:grow-0">{name}</span>
          <span className={gebucht ? PREIS_GEBUCHT : PREIS_RUHE}>{preis}</span>
        </span>
        {gebucht ? (
          <span className="shrink-0 inline-flex items-center gap-x-1 py-1 px-2 rounded-full text-xs font-medium bg-teal-100 text-teal-800 lg:bg-transparent lg:px-0">
            <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 6 9 17l-5-5" />
            </svg>
            Gebucht
          </span>
        ) : ausverkauft ? (
          <span className="shrink-0 inline-flex items-center py-0.5 px-2 rounded-full text-xs font-medium bg-muted text-muted-foreground-1">Ausverkauft</span>
        ) : (
          <span className="shrink-0 flex justify-center items-center size-8 rounded-full border border-layer-line bg-layer shadow-2xs text-foreground lg:size-7" aria-hidden="true">
            <svg className="shrink-0 size-4 lg:size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14" />
              <path d="M12 5v14" />
            </svg>
          </span>
        )}
      </button>
      {meldung && <div className="px-4 pb-3 lg:px-0 lg:pb-0 lg:pt-2">{meldung}</div>}
    </li>
  );
}

/** Dieselbe Zeile ohne Bedienelement (Zustand „nicht buchbar“): Name links, Preis rechts, in jeder Breite eine Zeile. */
export function StrichNurZeile({ name, preis }: { name: string; preis: string }) {
  return (
    <li className="flex items-center gap-x-3 px-4 py-3 border-b last:border-b-0 border-line-2 lg:bg-card lg:border lg:last:border-b lg:border-card-line lg:rounded-xl lg:shadow-2xs">
      <span className="grow min-w-0 text-sm font-medium text-foreground">{name}</span>
      <span className="shrink-0 text-sm tabular-nums text-muted-foreground-1">{preis}</span>
    </li>
  );
}

export interface KategorieSprung {
  id: string;
  label: string;
  symbol: ReactNode;
  anzahl: number;
}

const PILLE =
  "py-1.5 px-2.5 lg:px-3 shrink-0 inline-flex items-center gap-x-1.5 text-sm font-medium rounded-full border border-layer-line bg-layer text-layer-foreground shadow-2xs hover:bg-layer-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus";

/**
 * Kategorie-Leiste: Anker-Links auf `#<id>` der Abschnitte (der Aufrufer setzt `id` und `scroll-mt-44`, mit eingeklappter Kopfzeile `scroll-mt-28`, an die Abschnitte).
 * Hängt unter der Kopfzeile (`sticky top-15`, ab `lg` unter der haftenden Kopfzeile `top-29`, mit eingeklappter zweiter Zeile `top-14`; Abweichung AW-115). Die Artikelzahl steht nur ab `lg` sichtbar (KN9), im zugänglichen Namen immer.
 */
export function KategorieLeiste({ eintraege }: { eintraege: KategorieSprung[] }) {
  return (
    <>
      {/* Preline: component/badge/badge-soft-color-variants */}
      <nav aria-label="Kategorien" className="sticky top-15 lg:top-29 lg:transition-[top] lg:motion-reduce:transition-none group-data-[zeile2=zu]/shell:lg:top-14 z-10 -mx-4 px-4 py-2 sm:-mx-5 sm:px-5 bg-background-1 border-b border-line-2 lg:border-b-0">
        <ul className="flex items-center gap-x-2 overflow-x-auto lg:flex-wrap">
          {eintraege.map((k) => (
            <li key={k.id} className="shrink-0">
              <a href={`#${k.id}`} className={PILLE} aria-label={`${k.label}, ${k.anzahl} Artikel`}>
                <span className="shrink-0 text-muted-foreground-1" aria-hidden="true">
                  {k.symbol}
                </span>
                {k.label}
                <span className="max-lg:hidden text-xs tabular-nums text-muted-foreground-1" aria-hidden="true">
                  {k.anzahl}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}

/**
 * Schmale Leiste über der unteren Tab-Leiste (nur unter `lg`): links der Satz („Heute: 2 Striche · 1,30 €“), rechts die
 * Aktion (Rückgängig). Sie hängt `bottom-14` über der Unterleiste der Shell (`z-40` unter deren `z-50`); die Seite lässt
 * unten Platz (`StrichLeistenPlatz`). Kein Fokusfang, `role="status"`.
 */
export function HeuteLeiste({ children, aktion }: { children: ReactNode; aktion?: ReactNode }) {
  return (
    <>
      {/* Eigenbau Aktionsleiste unten (mobil): Abweichung AW-097 (kein Preline-Block für eine feste Leiste über der Tab-Leiste) */}
      <div role="status" className="fixed inset-x-0 bottom-14 z-40 lg:hidden flex items-center justify-between gap-x-3 px-4 py-2 min-h-14 bg-layer border-t border-layer-line shadow-lg">
        <p className="text-sm text-foreground">{children}</p>
        {aktion}
      </div>
    </>
  );
}

/** Platzhalter am Seitenende, damit `HeuteLeiste` nichts verdeckt (nur unter `lg`). */
export function StrichLeistenPlatz() {
  return <div className="h-16 lg:hidden" aria-hidden="true" />;
}
